import { Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import * as actions from './checklists.actions';
import { BusinessChecklistVM, ChecklistItem } from '@models/checklist';
import { HttpHandlerService } from '@services/httpHandler/httpHandler.service';
import { CollectionPaths } from '@models/collections';
import { FirestoreStateBaseForBusinessWithSubscription } from '@state/FirestoreStateBaseForBusiness';
import { DEFAULT_STATE_BASE } from '@state/FirestoreStateBase';
import { BusinessChecklistsModel, SortOption } from './checklists.types';

type Ctx = StateContext<BusinessChecklistsModel>;

const DEFAULT_STATE: BusinessChecklistsModel = {
	...DEFAULT_STATE_BASE,
	data: [],
	sortOption: SortOption.A_TO_Z,
	selectedFileType: 'all',
};

@State<BusinessChecklistsModel>({
	name: 'businessChecklists',
	defaults: DEFAULT_STATE,
})
@Injectable()
export class BusinessChecklistsState extends FirestoreStateBaseForBusinessWithSubscription<
	ChecklistItem,
	BusinessChecklistsModel
> {
	constructor(private http: HttpHandlerService) {
		super(
			(pathParams) =>
				CollectionPaths.business_checklists(pathParams.businessId),
			(doc) => ({ data: doc as ChecklistItem[] }),
			() => DEFAULT_STATE,
		);
	}

	@Action(actions.Create)
	async create(_ctx: Ctx, { payload }: actions.Create) {
		const checklistItems: BusinessChecklistVM = payload.checklistItems;

		const template: ChecklistItem = {
			businessId: this.businessId,
			lastEdited: checklistItems.lastEdited,
			created: checklistItems.created,
			draft: checklistItems.draft,
			default: checklistItems.default,
			fileType: checklistItems.fileType,
			id: checklistItems.id,
			title: checklistItems.title,
			type: 'template',
			headNode: true,
			nextNodeId: '',
		};

		const stagesAndTasks: ChecklistItem[] = [];
		checklistItems.stages.forEach((s, stageIndex, stages) => {
			stagesAndTasks.push({
				id: `${checklistItems.id}-${stageIndex}`,
				businessId: this.businessId,
				parentId: template.id,
				title: s.title,
				type: 'stage',
				headNode: stageIndex === 0,
				nextNodeId:
					stageIndex === stages.length - 1
						? ''
						: `${checklistItems.id}-${stageIndex + 1}`,
			});

			s.tasks.forEach((t, taskIndex, tasks) => {
				stagesAndTasks.push({
					id: `${checklistItems.id}-${stageIndex}-${taskIndex}`,
					businessId: this.businessId,
					parentId: `${checklistItems.id}-${stageIndex}`,
					title: t.title,
					type: 'task',
					headNode: taskIndex === 0,
					nextNodeId:
						taskIndex === tasks.length - 1
							? ''
							: `${checklistItems.id}-${stageIndex}-${taskIndex + 1}`,
					fieldPath: t.fieldPath,
				});
			});
		});

		const body: { checklistItems: ChecklistItem[]; oldItemIds: string[] } = {
			checklistItems: [template, ...stagesAndTasks],
			oldItemIds: payload.oldItemIds || [],
		};

		await this.http.post('checklist/add-checklist-template', body).toPromise();
	}

	@Action(actions.SetDefault)
	setDefault(_ctx: Ctx, { id }: actions.SetDefault) {
		this.repo.CreateOrMerge(id, { default: true });
	}

	@Action(actions.DeleteItems)
	deleteItems(ctx: Ctx, { ids }: actions.DeleteItems) {
		ids.forEach((id) => {
			ctx.dispatch(new actions.Remove(id));
		});
	}

	@Action(actions.Remove)
	removeTemplate(_ctx: Ctx, { templateId }: actions.Remove): void {
		this.repo.Delete(templateId);
	}

	@Action(actions.UpdateSortOption)
	updateSortOption(ctx: Ctx, { payload }: actions.UpdateSortOption): void {
		ctx.patchState({ sortOption: payload });
	}

	@Action(actions.UpdateSelectedFileType)
	updateSelectedFileType(
		ctx: Ctx,
		{ payload }: actions.UpdateSelectedFileType,
	): void {
		ctx.patchState({ selectedFileType: payload });
	}
}
